import {groups, max, min, quantile} from "d3-array";
import {select} from "d3-selection";

import type {DataPoint} from "@d3plus/data";
import {merge} from "@d3plus/data";
import {assign, elem} from "@d3plus/dom";
import type {D3Selection} from "@d3plus/dom";
import {accessor, BaseClass, configPrep, constant} from "../utils/index.js";
import type {AccessorFn} from "../utils/index.js";

import Circle from "./Circle.js";
import Rect from "./Rect.js";
import Whisker from "./Whisker.js";

const shapes: Record<string, typeof Circle | typeof Rect> = {Circle, Rect};

/**
    Creates SVG box based on an array of data.
*/
export default class Box extends BaseClass {
  _medianConfig: Record<string, unknown>;
  _orient: AccessorFn;
  _outlier: AccessorFn;
  _outlierConfig: Record<string, unknown>;
  _rectConfig: Record<string, unknown>;
  _rectWidth: AccessorFn;
  _whiskerConfig: Record<string, unknown>;
  _whiskerMode: (string | number)[];
  _x: AccessorFn;
  _y: AccessorFn;
  _data!: DataPoint[];
  _select!: D3Selection;
  _box!: Rect;
  _median!: Rect;
  _whisker!: Whisker;
  _whiskerEndpoint: (Circle | Rect)[];
  _duration!: number;

  /**
      Invoked when creating a new class instance, and overrides any default parameters inherited from BaseClass.
      @private
*/
  constructor() {
    super();

    this._medianConfig = {
      fill: constant("black"),
    };
    this._orient = accessor("orient", "vertical");
    this._outlier = accessor("outlier", "Circle");
    this._outlierConfig = {
      Circle: {
        r: accessor("r", 5),
      },
      Rect: {
        height: (d: DataPoint, i: number) =>
          this._orient(d, i) === "vertical" ? 5 : 20,
        width: (d: DataPoint, i: number) =>
          this._orient(d, i) === "vertical" ? 20 : 5,
      },
    };
    this._rectConfig = {
      fill: constant("white"),
      stroke: constant("black"),
      strokeWidth: constant(1),
    };
    this._rectWidth = constant(50);
    this._whiskerConfig = {};
    this._whiskerMode = ["tukey", "tukey"];
    this._x = accessor("x", 250);
    this._y = accessor("y", 250);
    this._whiskerEndpoint = [];
  }

  /**
      Draws the Box.
*/
  render(): this {
    if (this._select === void 0) {
      this.select(
        select("body")
          .append("svg")
          .style("width", `${window.innerWidth}px`)
          .style("height", `${window.innerHeight}px`)
          .style("display", "block")
          .node(),
      );
    }

    const outlierData: DataPoint[] = [];

    const filteredData = groups(this._data, (d: DataPoint, i: number) =>
      this._orient(d, i) === "vertical" ? this._x(d, i) : this._y(d, i),
    ).map(([key, groupData]: [DataPoint[keyof DataPoint], DataPoint[]]) => {
      const d: DataPoint = {key, values: groupData} as unknown as DataPoint;
      (d as Record<string, unknown>).data = merge(
        d.values as unknown as DataPoint[],
      );
      (d as Record<string, unknown>).i = this._data.indexOf(
        (d.values as unknown as DataPoint[])[0],
      );
      (d as Record<string, unknown>).orient = this._orient(
        d.data as DataPoint,
        d.i as number,
      );
      const values: number[] = (d.values as unknown as DataPoint[]).map(
        (d as Record<string, unknown>).orient === "vertical"
          ? this._y
          : this._x,
      ) as unknown as number[];
      values.sort((a: number, b: number) => a - b);

      (d as Record<string, unknown>).first = quantile(values, 0.25);
      (d as Record<string, unknown>).median = quantile(values, 0.5);
      (d as Record<string, unknown>).third = quantile(values, 0.75);

      const mode = this._whiskerMode;

      if (mode[0] === "tukey") {
        (d as Record<string, unknown>).lowerLimit =
          (d.first as number) -
          ((d.third as number) - (d.first as number)) * 1.5;
        if ((d.lowerLimit as number) < min(values)!)
          (d as Record<string, unknown>).lowerLimit = min(values);
      } else if (mode[0] === "extent")
        (d as Record<string, unknown>).lowerLimit = min(values);
      else if (typeof mode[0] === "number")
        (d as Record<string, unknown>).lowerLimit = quantile(values, mode[0]);

      if (mode[1] === "tukey") {
        (d as Record<string, unknown>).upperLimit =
          (d.third as number) +
          ((d.third as number) - (d.first as number)) * 1.5;
        if ((d.upperLimit as number) > max(values)!)
          (d as Record<string, unknown>).upperLimit = max(values);
      } else if (mode[1] === "extent")
        (d as Record<string, unknown>).upperLimit = max(values);
      else if (typeof mode[1] === "number")
        (d as Record<string, unknown>).upperLimit = quantile(values, mode[1]);

      const rectLength = (d.third as number) - (d.first as number);

      // Compute values for vertical orientation.
      if (d.orient === "vertical") {
        (d as Record<string, unknown>).height = rectLength;
        (d as Record<string, unknown>).width = this._rectWidth(
          d.data as DataPoint,
          d.i as number,
        );
        (d as Record<string, unknown>).x = this._x(
          d.data as DataPoint,
          d.i as number,
        );
        (d as Record<string, unknown>).y = (d.first as number) + rectLength / 2;
      } else if (d.orient === "horizontal") {
        // Compute values for horizontal orientation.
        (d as Record<string, unknown>).height = this._rectWidth(
          d.data as DataPoint,
          d.i as number,
        );
        (d as Record<string, unknown>).width = rectLength;
        (d as Record<string, unknown>).x = (d.first as number) + rectLength / 2;
        (d as Record<string, unknown>).y = this._y(
          d.data as DataPoint,
          d.i as number,
        );
      }

      // Compute data for outliers.
      (d.values as unknown as DataPoint[]).forEach(
        (eachValue: DataPoint, index: number) => {
          const value =
            d.orient === "vertical"
              ? (this._y(eachValue, index) as number)
              : (this._x(eachValue, index) as number);

          if (
            value < (d.lowerLimit as number) ||
            value > (d.upperLimit as number)
          ) {
            const dataObj: DataPoint = {} as DataPoint;
            (dataObj as Record<string, unknown>).__d3plus__ = true;
            (dataObj as Record<string, unknown>).data = eachValue;
            (dataObj as Record<string, unknown>).i = index;
            (dataObj as Record<string, unknown>).outlier = this._outlier(
              eachValue,
              index,
            );

            if (d.orient === "vertical") {
              (dataObj as Record<string, unknown>).x = d.x;
              (dataObj as Record<string, unknown>).y = value;
              outlierData.push(dataObj);
            } else if (d.orient === "horizontal") {
              (dataObj as Record<string, unknown>).y = d.y;
              (dataObj as Record<string, unknown>).x = value;
              outlierData.push(dataObj);
            }
          }
        },
      );

      (d as Record<string, unknown>).__d3plus__ = true;

      return d;
    });

    // Draw box.
    this._box = new Rect()
      .data(filteredData)
      .x((d: DataPoint) => d.x)
      .y((d: DataPoint) => d.y)
      .select(elem("g.d3plus-Box", {parent: this._select}).node())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .config(configPrep.bind(this as any)(this._rectConfig, "shape")!)
      .render();

    // Draw median.
    this._median = new Rect()
      .data(filteredData)
      .x((d: DataPoint) => (d.orient === "vertical" ? d.x : d.median))
      .y((d: DataPoint) => (d.orient === "vertical" ? d.median : d.y))
      .height((d: DataPoint) => (d.orient === "vertical" ? 1 : d.height))
      .width((d: DataPoint) => (d.orient === "vertical" ? d.width : 1))
      .select(elem("g.d3plus-Box-Median", {parent: this._select}).node())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .config(configPrep.bind(this as any)(this._medianConfig, "shape")!)
      .render();

    // Draw 2 lines using Whisker class.
    // Construct coordinates for whisker startpoints and push it to the whiskerData.
    const whiskerData: DataPoint[] = [];
    filteredData.forEach((d: DataPoint, i: number) => {
      const x = d.x;
      const y = d.y;
      const topLength = (d.first as number) - (d.lowerLimit as number);
      const bottomLength = (d.upperLimit as number) - (d.third as number);

      if (d.orient === "vertical") {
        const topY = (y as number) - (d.height as number) / 2;
        const bottomY = (y as number) + (d.height as number) / 2;
        whiskerData.push(
          {
            __d3plus__: true,
            data: d,
            i,
            x,
            y: topY,
            length: topLength,
            orient: "top",
          } as unknown as DataPoint,
          {
            __d3plus__: true,
            data: d,
            i,
            x,
            y: bottomY,
            length: bottomLength,
            orient: "bottom",
          } as unknown as DataPoint,
        );
      } else if (d.orient === "horizontal") {
        const topX = (x as number) + (d.width as number) / 2;
        const bottomX = (x as number) - (d.width as number) / 2;
        whiskerData.push(
          {
            __d3plus__: true,
            data: d,
            i,
            x: topX,
            y,
            length: bottomLength,
            orient: "right",
          } as unknown as DataPoint,
          {
            __d3plus__: true,
            data: d,
            i,
            x: bottomX,
            y,
            length: topLength,
            orient: "left",
          } as unknown as DataPoint,
        );
      }
    });

    // Draw whiskers.
    this._whisker = new Whisker()
      .data(whiskerData)
      .select(elem("g.d3plus-Box-Whisker", {parent: this._select}).node())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .config(configPrep.bind(this as any)(this._whiskerConfig, "shape")!)
      .render();

    // Draw outliers.
    this._whiskerEndpoint = [];
    groups(outlierData, (d: DataPoint) => d.outlier).forEach(
      ([shapeName, values]: [DataPoint[keyof DataPoint], DataPoint[]]) => {
        this._whiskerEndpoint.push(
          new shapes[shapeName as string]()
            .data(values)
            .select(
              elem(`g.d3plus-Box-Outlier-${shapeName}`, {
                parent: this._select,
              }).node(),
            )
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .config(configPrep.bind(this as any)(this._outlierConfig, "shape", shapeName as string)!)
            .render(),
        );
      },
    );

    return this;
  }

  /**
      The active highlight state for all sub-shapes in this Box.
*/
  active(_: ((d: DataPoint, i: number) => boolean) | null): void {
    if (this._box) this._box.active(_);
    if (this._median) this._median.active(_);
    if (this._whisker) this._whisker.active(_);
    if (this._whiskerEndpoint)
      this._whiskerEndpoint.forEach((endPoint: Circle | Rect) =>
        endPoint.active(_),
      );
  }

  /**
      The data array used to create shapes.
*/
  data(): DataPoint[];
  data(_: DataPoint[]): this;
  data(_?: DataPoint[]): DataPoint[] | this {
    return arguments.length ? ((this._data = _!), this) : this._data;
  }

  /**
      The hover highlight state for all sub-shapes in this Box.
*/
  hover(_: ((d: DataPoint, i: number) => boolean) | null): void {
    if (this._box) this._box.hover(_);
    if (this._median) this._median.hover(_);
    if (this._whisker) this._whisker.hover(_);
    if (this._whiskerEndpoint)
      this._whiskerEndpoint.forEach((endPoint: Circle | Rect) =>
        endPoint.hover(_),
      );
  }

  /**
      Configuration object for the median line.
*/
  medianConfig(): Record<string, unknown>;
  medianConfig(_: Record<string, unknown>): this;
  medianConfig(_?: Record<string, unknown>): Record<string, unknown> | this {
    return arguments.length
      ? ((this._medianConfig = assign(this._medianConfig, _!)), this)
      : this._medianConfig;
  }

  /**
      The orientation of the box shape.
*/
  orient(): AccessorFn;
  orient(_: AccessorFn | string): this;
  orient(_?: AccessorFn | string): AccessorFn | this {
    return arguments.length
      ? ((this._orient = typeof _ === "function" ? _ : constant(_) as unknown as AccessorFn), this)
      : this._orient;
  }

  /**
      Whether to show outlier points.
*/
  outlier(): AccessorFn;
  outlier(_: AccessorFn | string): this;
  outlier(_?: AccessorFn | string): AccessorFn | this {
    return arguments.length
      ? ((this._outlier = typeof _ === "function" ? _ : constant(_) as unknown as AccessorFn), this)
      : this._outlier;
  }

  /**
      Configuration object for each outlier point.
*/
  outlierConfig(): Record<string, unknown>;
  outlierConfig(_: Record<string, unknown>): this;
  outlierConfig(_?: Record<string, unknown>): Record<string, unknown> | this {
    return arguments.length
      ? ((this._outlierConfig = assign(this._outlierConfig, _!)), this)
      : this._outlierConfig;
  }

  /**
      Configuration object for the rect shape.
*/
  rectConfig(): Record<string, unknown>;
  rectConfig(_: Record<string, unknown>): this;
  rectConfig(_?: Record<string, unknown>): Record<string, unknown> | this {
    return arguments.length
      ? ((this._rectConfig = assign(this._rectConfig, _!)), this)
      : this._rectConfig;
  }

  /**
      The width accessor for each box.

@example
function(d) {
  return d.width;
}
*/
  rectWidth(): AccessorFn;
  rectWidth(_: AccessorFn | number): this;
  rectWidth(_?: AccessorFn | number): AccessorFn | this {
    return arguments.length
      ? ((this._rectWidth = typeof _ === "function" ? _ : constant(_) as unknown as AccessorFn), this)
      : this._rectWidth;
  }

  /**
      The SVG container element for this visualization. 3 selector or DOM element.
*/
  select(): D3Selection;
  select(_: string | HTMLElement | SVGElement | null): this;
  select(_?: string | HTMLElement | SVGElement | null): D3Selection | this {
    return arguments.length
      ? ((this._select = select(_ as string) as unknown as D3Selection), this)
      : this._select;
  }

  /**
      Configuration object for the whisker.
*/
  whiskerConfig(): Record<string, unknown>;
  whiskerConfig(_: Record<string, unknown>): this;
  whiskerConfig(_?: Record<string, unknown>): Record<string, unknown> | this {
    return arguments.length
      ? ((this._whiskerConfig = assign(this._whiskerConfig, _!)), this)
      : this._whiskerConfig;
  }

  /**
      Determines the value used for each whisker. Can be passed a single value to apply for both whiskers, or an Array of 2 values for the lower and upper whiskers (in that order). Accepted values are `"tukey"`, `"extent"`, or a Number representing a quantile.
*/
  whiskerMode(): (string | number)[];
  whiskerMode(_: (string | number)[] | string | number): this;
  whiskerMode(
    _?: (string | number)[] | string | number,
  ): (string | number)[] | this {
    return arguments.length
      ? ((this._whiskerMode = _ instanceof Array ? _ : [_!, _!]), this)
      : this._whiskerMode;
  }

  /**
      The x position accessor for each box.

@example
function(d) {
  return d.x;
}
*/
  x(): AccessorFn;
  x(_: AccessorFn | number): this;
  x(_?: AccessorFn | number): AccessorFn | this {
    return arguments.length
      ? ((this._x =
          typeof _ === "function" ? _ : accessor(_ as unknown as string)),
        this)
      : this._x;
  }

  /**
      The y position accessor for each box.

@example
function(d) {
  return d.y;
}
*/
  y(): AccessorFn;
  y(_: AccessorFn | number): this;
  y(_?: AccessorFn | number): AccessorFn | this {
    return arguments.length
      ? ((this._y =
          typeof _ === "function" ? _ : accessor(_ as unknown as string)),
        this)
      : this._y;
  }
}
